import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Divider,
  FormControlLabel,
  IconButton,
  Box,
  Switch,
  Typography,
  CircularProgress,
} from "@mui/material";
import {
  Close,
  Add,
  DeleteOutline,
  CloudUploadOutlined,
  ArrowBackIosNew,
  ArrowForwardIos,
  StarBorderRounded,
} from "@mui/icons-material";
import axios from "axios";
import toast from "react-hot-toast";
import { ENV_REACT_APP } from "../../../../Configurations/ENV_Config";
import { UpgradePrompt } from "../../../../Components/PremiumGate/PremiumGate";

const TEAL = "#004B4C";
const ACCEPT = ["image/jpeg", "image/png", "image/webp"];
const ACCEPT_ATTR = "image/jpeg,image/png,image/webp";
const MAX_BYTES = 8 * 1024 * 1024; // 8MB (matches the server multer limit)
const DEFAULT_CAP = 5;

const apiBase = (path: string) =>
  `${ENV_REACT_APP?.REACT_APP_API_BASE_URL || ""}${path}`;

export interface OwnerPhotoItem {
  _id: string;
  fileName: string;
  uploadedAt: string;
  url: string;
}

type Props = {
  open: boolean;
  onClose: () => void;
  propertyId: string;
  authToken?: string;
  photos: OwnerPhotoItem[];
  onChange: () => void; // reload the photos in the parent (slider)
  // Owner-controlled Street View opt-out. `hideStreetView` is the
  // current value (server or optimistic). `onHideStreetViewChange` is
  // called with the new value after the toggle so the parent gallery
  // updates immediately.
  hideStreetView?: boolean;
  onHideStreetViewChange?: (hide: boolean) => void;
  // October 2026 free/premium layer: a free owner (once gating is live) may
  // upload only this many photos. Undefined = premium / gating off → the
  // site-wide cap applies.
  freeLimit?: number;
};

const OwnerPhotoManager: React.FC<Props> = ({
  open,
  onClose,
  propertyId,
  authToken,
  photos,
  onChange,
  hideStreetView = false,
  onHideStreetViewChange,
  freeLimit,
}) => {
  const [siteCap, setCap] = useState<number>(DEFAULT_CAP);
  const limitedByPlan = typeof freeLimit === "number" && freeLimit < siteCap;
  const cap = limitedByPlan ? (freeLimit as number) : siteCap;
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState<{ done: number; total: number } | null>(
    null
  );
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [reordering, setReordering] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const authHeader = useMemo(
    () => (authToken ? { Authorization: `Bearer ${authToken}` } : undefined),
    [authToken]
  );
  const remaining = Math.max(0, cap - photos.length);

  // Pull the live cap (superadmin-configurable) when the dialog opens.
  useEffect(() => {
    if (!open) return;
    axios
      .get(apiBase("/api/site-config/public"))
      .then((r) => {
        const c = r?.data?.data?.ownerPhotoCap;
        if (Number.isInteger(c)) setCap(c);
      })
      .catch(() => {});
  }, [open]);

  const uploadFiles = useCallback(
    async (fileList: FileList | File[]) => {
      let files = Array.from(fileList);
      if (!files.length || uploading) return;

      // Client-side validation (server is still the source of truth).
      const valid: File[] = [];
      for (const f of files) {
        if (!ACCEPT.includes(f.type)) {
          toast.error(`${f.name}: use JPEG, PNG, or WebP.`);
          continue;
        }
        if (f.size > MAX_BYTES) {
          toast.error(`${f.name}: must be under 8 MB.`);
          continue;
        }
        valid.push(f);
      }
      if (!valid.length) return;
      if (valid.length > remaining) {
        toast.error(
          `Only ${remaining} more photo${remaining === 1 ? "" : "s"} allowed.`
        );
        if (remaining <= 0) return;
        valid.splice(remaining); // keep only what fits
      }

      setUploading(true);
      setProgress({ done: 0, total: valid.length });
      let done = 0;
      for (const f of valid) {
        try {
          const fd = new FormData();
          fd.append("photo", f);
          await axios.post(
            apiBase(`/api/user/property/${propertyId}/owner-photos`),
            fd,
            authHeader ? { headers: authHeader } : undefined
          );
          done += 1;
          setProgress({ done, total: valid.length });
        } catch (e: any) {
          const msg =
            e?.response?.data?.errors?.message ||
            e?.response?.data?.message ||
            "Upload failed.";
          toast.error(msg);
          break; // stop on first failure (e.g. cap reached)
        }
      }
      setUploading(false);
      setProgress(null);
      if (done > 0) {
        toast.success(`${done} photo${done === 1 ? "" : "s"} added.`);
        onChange();
      }
    },
    [propertyId, remaining, uploading, authHeader, onChange]
  );

  const handleDelete = async (id: string) => {
    if (deletingId) return;
    setDeletingId(id);
    try {
      await axios.delete(
        apiBase(`/api/user/property/${propertyId}/owner-photos/${id}`),
        authHeader ? { headers: authHeader } : undefined
      );
      toast.success("Photo removed.");
      onChange();
    } catch (e: any) {
      toast.error(e?.response?.data?.errors?.message || "Delete failed.");
    } finally {
      setDeletingId(null);
    }
  };

  const handleMove = async (index: number, direction: -1 | 1) => {
    const target = index + direction;
    if (target < 0 || target >= photos.length) return;

    // Build the new order. Splice-swap the two adjacent items.
    const nextIds = photos.map((p) => p._id);
    [nextIds[index], nextIds[target]] = [nextIds[target], nextIds[index]];
    await saveOrder(nextIds);
  };

  // The featured photo is simply the first one: it heads the property page
  // header, leads the gallery, and is the card image in search results.
  const handleMakeFeatured = async (index: number) => {
    if (index === 0) return;
    const nextIds = photos.map((p) => p._id);
    const [picked] = nextIds.splice(index, 1);
    nextIds.unshift(picked);
    if (await saveOrder(nextIds)) toast.success("Featured photo updated.");
  };

  const saveOrder = async (nextIds: string[]): Promise<boolean> => {
    if (reordering || uploading || deletingId) return false;
    setReordering(true);
    try {
      await axios.patch(
        apiBase(`/api/user/property/${propertyId}/owner-photos/order`),
        { orderedPhotoIds: nextIds },
        authHeader ? { headers: authHeader } : undefined
      );
      onChange();
      return true;
    } catch (e: any) {
      toast.error(
        e?.response?.data?.errors?.message || "Reorder failed. Try again."
      );
      return false;
    } finally {
      setReordering(false);
    }
  };

  const [svToggling, setSvToggling] = useState(false);
  const handleToggleStreetView = async (nextHide: boolean) => {
    if (svToggling) return;
    // Optimistically flip the gallery so the owner sees the change
    // immediately; if the PATCH fails we roll back.
    onHideStreetViewChange?.(nextHide);
    setSvToggling(true);
    try {
      await axios.patch(
        apiBase(`/api/user/property/${propertyId}/street-view-visibility`),
        { hide: nextHide },
        authHeader ? { headers: authHeader } : undefined
      );
    } catch (e: any) {
      onHideStreetViewChange?.(!nextHide);
      toast.error(
        e?.response?.data?.errors?.message ||
          "Couldn't save that. Try again."
      );
    } finally {
      setSvToggling(false);
    }
  };

  const openPicker = () => inputRef.current?.click();

  return (
    <Dialog
      open={open}
      onClose={uploading ? undefined : onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{ sx: { borderRadius: 3 } }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          fontWeight: 700,
          color: TEAL,
        }}
      >
        Manage your photos
        <IconButton onClick={onClose} disabled={uploading} size="small">
          <Close />
        </IconButton>
      </DialogTitle>

      <DialogContent>
        <Typography
          variant="body2"
          sx={{ color: "text.secondary", mb: 2, fontWeight: 600 }}
        >
          {photos.length} of {cap} used
          {progress && ` · uploading ${progress.done}/${progress.total}…`}
        </Typography>

        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fill, minmax(96px, 1fr))",
            gap: 1.5,
          }}
        >
          {photos.map((p, index) => {
            const busy = !!deletingId || uploading || reordering;
            const isFirst = index === 0;
            const isLast = index === photos.length - 1;
            return (
              <Box
                key={p._id}
                sx={{
                  position: "relative",
                  aspectRatio: "1 / 1",
                  borderRadius: 2,
                  overflow: "hidden",
                  border: isFirst
                    ? `2px solid ${TEAL}`
                    : "1px solid rgba(0,75,76,0.12)",
                }}
                data-testid={`owner-photo-tile-${p._id}`}
              >
                <Box
                  component="img"
                  src={p.url}
                  alt={p.fileName}
                  sx={{
                    width: "100%",
                    height: "100%",
                    objectFit: "cover",
                    display: "block",
                  }}
                />
                {isFirst && (
                  <Box
                    sx={{
                      position: "absolute",
                      bottom: 4,
                      left: 4,
                      bgcolor: TEAL,
                      color: "#fff",
                      fontSize: 10,
                      fontWeight: 700,
                      px: 0.75,
                      py: 0.25,
                      borderRadius: 0.75,
                      letterSpacing: 0.2,
                    }}
                  >
                    FEATURED
                  </Box>
                )}
                {!isFirst && (
                  <IconButton
                    aria-label="Make this the featured photo"
                    title="Make featured"
                    onClick={() => handleMakeFeatured(index)}
                    disabled={busy}
                    size="small"
                    sx={{
                      position: "absolute",
                      bottom: 4,
                      left: 4,
                      bgcolor: "rgba(0,0,0,0.55)",
                      color: "#fff",
                      "&:hover": { bgcolor: TEAL },
                    }}
                    data-testid={`owner-photo-feature-${p._id}`}
                  >
                    <StarBorderRounded sx={{ fontSize: 16 }} />
                  </IconButton>
                )}
                {/* Reorder controls: left/right arrows. First tile
                    disables ←, last disables →. Featured photo is
                    tile 0 — that's what card lists show and what
                    leads the property slider. */}
                <IconButton
                  aria-label="Move photo left"
                  onClick={() => handleMove(index, -1)}
                  disabled={busy || isFirst}
                  size="small"
                  sx={{
                    position: "absolute",
                    top: "50%",
                    left: 4,
                    transform: "translateY(-50%)",
                    bgcolor: "rgba(0,0,0,0.55)",
                    color: "#fff",
                    "&:hover": { bgcolor: "rgba(0,0,0,0.75)" },
                    "&.Mui-disabled": {
                      bgcolor: "rgba(0,0,0,0.25)",
                      color: "rgba(255,255,255,0.5)",
                    },
                  }}
                  data-testid={`owner-photo-move-left-${p._id}`}
                >
                  <ArrowBackIosNew sx={{ fontSize: 14 }} />
                </IconButton>
                <IconButton
                  aria-label="Move photo right"
                  onClick={() => handleMove(index, 1)}
                  disabled={busy || isLast}
                  size="small"
                  sx={{
                    position: "absolute",
                    top: "50%",
                    right: 4,
                    transform: "translateY(-50%)",
                    bgcolor: "rgba(0,0,0,0.55)",
                    color: "#fff",
                    "&:hover": { bgcolor: "rgba(0,0,0,0.75)" },
                    "&.Mui-disabled": {
                      bgcolor: "rgba(0,0,0,0.25)",
                      color: "rgba(255,255,255,0.5)",
                    },
                  }}
                  data-testid={`owner-photo-move-right-${p._id}`}
                >
                  <ArrowForwardIos sx={{ fontSize: 14 }} />
                </IconButton>
                <IconButton
                  aria-label="Delete photo"
                  onClick={() => handleDelete(p._id)}
                  disabled={busy}
                  size="small"
                  sx={{
                    position: "absolute",
                    top: 4,
                    right: 4,
                    bgcolor: "rgba(0,0,0,0.55)",
                    color: "#fff",
                    "&:hover": { bgcolor: "rgba(0,0,0,0.75)" },
                  }}
                >
                  {deletingId === p._id ? (
                    <CircularProgress size={16} sx={{ color: "#fff" }} />
                  ) : (
                    <DeleteOutline sx={{ fontSize: 18 }} />
                  )}
                </IconButton>
              </Box>
            );
          })}

          {remaining > 0 && (
            <Box
              role="button"
              tabIndex={0}
              onClick={openPicker}
              onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && openPicker()}
              onDragOver={(e) => {
                e.preventDefault();
                setDragOver(true);
              }}
              onDragLeave={() => setDragOver(false)}
              onDrop={(e) => {
                e.preventDefault();
                setDragOver(false);
                if (e.dataTransfer.files?.length) uploadFiles(e.dataTransfer.files);
              }}
              sx={{
                aspectRatio: "1 / 1",
                borderRadius: 2,
                border: "2px dashed",
                borderColor: dragOver ? TEAL : "rgba(0,75,76,0.3)",
                bgcolor: dragOver ? "rgba(0,75,76,0.06)" : "transparent",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                cursor: uploading ? "default" : "pointer",
                color: TEAL,
                opacity: uploading ? 0.5 : 1,
                transition: "border-color 0.15s, background-color 0.15s",
                textAlign: "center",
                px: 1,
              }}
            >
              {uploading ? (
                <CircularProgress size={22} sx={{ color: TEAL }} />
              ) : (
                <>
                  <Add sx={{ fontSize: 26 }} />
                  <Typography sx={{ fontSize: 11, fontWeight: 600, lineHeight: 1.2 }}>
                    Add
                  </Typography>
                </>
              )}
            </Box>
          )}
        </Box>

        <Box
          sx={{
            mt: 2,
            display: "flex",
            alignItems: "center",
            gap: 1,
            color: "text.secondary",
          }}
        >
          <CloudUploadOutlined sx={{ fontSize: 18 }} />
          <Typography variant="caption">
            {remaining > 0
              ? "Drag & drop or click “Add”. JPEG, PNG or WebP, up to 8 MB each. Star a photo to make it your header."
              : limitedByPlan
              ? cap === 0
                ? "Photo uploads are part of Premium."
                : `The free plan includes ${cap} photo${cap === 1 ? "" : "s"}.`
              : `You’ve reached the ${cap}-photo limit — delete one to add more.`}
          </Typography>
        </Box>
        {limitedByPlan && remaining === 0 && (
          <Box sx={{ mt: 3 }}>
            <UpgradePrompt
              compact
              title={`Add up to ${siteCap} photos with Premium`}
              description="Show off your home with a full gallery — and pick any of them for your header."
            />
          </Box>
        )}

        <input
          ref={inputRef}
          type="file"
          accept={ACCEPT_ATTR}
          multiple
          hidden
          onChange={(e) => {
            if (e.target.files?.length) uploadFiles(e.target.files);
            e.target.value = "";
          }}
        />

        {/* Owner-controlled Street View opt-out. Renders only when a
            handler is wired (it always is in the property detail page)
            — the switch flips the slide off in the gallery immediately
            and PATCHes to persist the preference. */}
        {onHideStreetViewChange && (
          <>
            <Divider sx={{ my: 2.5, borderColor: "rgba(0,75,76,0.12)" }} />
            <Box>
              <FormControlLabel
                sx={{ ml: 0 }}
                control={
                  <Switch
                    checked={hideStreetView}
                    onChange={(_, checked) => handleToggleStreetView(checked)}
                    disabled={svToggling}
                    data-testid="street-view-hide-switch"
                    sx={{
                      "& .MuiSwitch-switchBase.Mui-checked": { color: TEAL },
                      "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                        backgroundColor: TEAL,
                      },
                    }}
                  />
                }
                label={
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    Hide Street View for this property
                  </Typography>
                }
                labelPlacement="start"
                slotProps={{
                  typography: { sx: { flex: 1 } },
                }}
              />
              <Typography
                variant="caption"
                sx={{
                  display: "block",
                  color: "text.secondary",
                  mt: 0.5,
                  ml: 0.5,
                }}
              >
                Google sometimes over-blurs the panorama near a home. Turn
                this on if the Street View slide looks wrong.
              </Typography>
            </Box>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default OwnerPhotoManager;
